import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  getGscMonitorOverview,
  acknowledgeGscAlert,
} from "@/lib/gsc-monitor.functions";

export const Route = createFileRoute("/dashboard/admin/gsc-monitor")({
  head: () => ({
    meta: [
      { title: "GSC Submission Monitor · Heallexa Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: GscMonitorPage,
});

type LogRow = {
  id: string;
  kind: string;
  batch_from: number | null;
  batch_to: number | null;
  endpoint_status: number | null;
  endpoint_healthy: boolean | null;
  total_submitted: number | null;
  succeeded: number | null;
  failed_count: number | null;
  attempt: number | null;
  error_summary: string | null;
  duration_ms: number | null;
  created_at: string;
};

type AlertRow = {
  id: string;
  kind: string;
  message: string;
  status_code: number | null;
  created_at: string;
  acknowledged_at: string | null;
};

type Overview = {
  recentLogs: LogRow[];
  alerts: AlertRow[];
  lastSubmit: LogRow | null;
  lastHealth: LogRow | null;
  totals24h: { succeeded: number; failed: number; total: number };
  lastIndexNow: {
    id: string;
    submitted_count: number;
    ok: boolean;
    status_code: number | null;
    source: string;
    created_at: string;
  } | null;
  series: { day: string; gscSucceeded: number; gscFailed: number; indexnowUrls: number }[];
  sitemapChunkCount: number;
  rootSitemap: {
    path: string;
    lastSubmitted?: string;
    lastDownloaded?: string;
    isPending?: boolean;
    errors?: string;
    warnings?: string;
    contents?: { type: string; submitted?: string; indexed?: string }[];
  } | null;
};

function StatusBadge({ status }: { status: number | null }) {
  if (status == null) return <Badge variant="secondary">—</Badge>;
  if (status >= 200 && status < 300)
    return <Badge className="bg-green-600 hover:bg-green-700">{status}</Badge>;
  if (status === 404)
    return <Badge variant="destructive">404 not deployed</Badge>;
  return <Badge variant="destructive">{status}</Badge>;
}

function GscMonitorPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, primaryRole } = useAuth();
  const fetchOverview = useServerFn(getGscMonitorOverview);
  const ackAlert = useServerFn(acknowledgeGscAlert);

  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!user || primaryRole !== "admin")) {
      navigate({ to: "/admin-login" });
    }
  }, [authLoading, user, primaryRole, navigate]);

  const load = async () => {
    try {
      const res = (await fetchOverview()) as Overview;
      setData(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load monitor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || primaryRole !== "admin") return;
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, primaryRole]);

  const handleAck = async (alertId: string) => {
    try {
      await ackAlert({ data: { alertId } });
      toast.success("Alert acknowledged");
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to acknowledge");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const unackAlerts = data?.alerts.filter((a) => !a.acknowledged_at) ?? [];
  const lastSubmit = data?.lastSubmit;
  const lastHealth = data?.lastHealth;
  const healthOK = lastHealth?.endpoint_healthy === true;

  return (
    <div>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link
              to="/dashboard/admin"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back to admin
            </Link>
            <h1 className="text-2xl font-semibold font-display">GSC Submission Monitor</h1>
            <p className="text-sm text-muted-foreground">
              Live status of Google Search Console sitemap submissions. Auto-refreshes every 30s.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Status cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-md card-surface-flat p-4">
            <div className="text-xs uppercase text-muted-foreground mb-1">
              Endpoint health
            </div>
            <div className="flex items-center gap-2">
              {healthOK ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive" />
              )}
              <StatusBadge status={lastHealth?.endpoint_status ?? null} />
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {lastHealth
                ? new Date(lastHealth.created_at).toLocaleString()
                : "No checks yet"}
            </div>
          </div>

          <div className="rounded-md card-surface-flat p-4">
            <div className="text-xs uppercase text-muted-foreground mb-1">
              Last submission
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={lastSubmit?.endpoint_status ?? null} />
              {lastSubmit && (
                <span className="text-sm">
                  pages {lastSubmit.batch_from}–{lastSubmit.batch_to}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {lastSubmit
                ? `${lastSubmit.succeeded}/${lastSubmit.total_submitted} ok · ${
                    lastSubmit.failed_count ?? 0
                  } failed · ${new Date(lastSubmit.created_at).toLocaleString()}`
                : "No submissions yet"}
            </div>
          </div>

          <div className="rounded-md card-surface-flat p-4">
            <div className="text-xs uppercase text-muted-foreground mb-1">
              Last 24h totals
            </div>
            <div className="text-2xl font-semibold">
              {data?.totals24h.succeeded ?? 0}{" "}
              <span className="text-sm font-normal text-muted-foreground">
                / {data?.totals24h.total ?? 0} sitemaps submitted
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {data?.totals24h.failed ?? 0} individual failures
            </div>
          </div>
        </div>

        {/* Live GSC root sitemap + IndexNow + sitemap chunk count */}
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-md card-surface-flat p-4">
            <div className="text-xs uppercase text-muted-foreground mb-1">
              Root sitemap in GSC
            </div>
            {data?.rootSitemap ? (
              <>
                <div className="flex items-center gap-2">
                  {data.rootSitemap.errors === "0" ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-destructive" />
                  )}
                  <Badge variant={data.rootSitemap.isPending ? "secondary" : "default"}>
                    {data.rootSitemap.isPending ? "pending" : "processed"}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {data.rootSitemap.errors} err / {data.rootSitemap.warnings} warn
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  Submitted: {data.rootSitemap.lastSubmitted
                    ? new Date(data.rootSitemap.lastSubmitted).toLocaleString()
                    : "—"}
                  <br />
                  Downloaded: {data.rootSitemap.lastDownloaded
                    ? new Date(data.rootSitemap.lastDownloaded).toLocaleString()
                    : "—"}
                  {data.rootSitemap.contents?.[0] && (
                    <>
                      <br />
                      URLs: {Number(data.rootSitemap.contents[0].submitted ?? 0).toLocaleString()} submitted ·{" "}
                      {Number(data.rootSitemap.contents[0].indexed ?? 0).toLocaleString()} indexed
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No live data</div>
            )}
          </div>

          <div className="rounded-md card-surface-flat p-4">
            <div className="text-xs uppercase text-muted-foreground mb-1">
              Last IndexNow push
            </div>
            {data?.lastIndexNow ? (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-lg font-semibold">
                    {data.lastIndexNow.submitted_count.toLocaleString()}
                  </span>
                  <span className="text-sm text-muted-foreground">URLs</span>
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {new Date(data.lastIndexNow.created_at).toLocaleString()}
                  <br />
                  source: {data.lastIndexNow.source} · HTTP {data.lastIndexNow.status_code ?? "—"}
                </div>
              </>
            ) : (
              <div className="text-sm text-muted-foreground">No pushes recorded yet</div>
            )}
          </div>

          <div className="rounded-md card-surface-flat p-4">
            <div className="text-xs uppercase text-muted-foreground mb-1">
              Sitemap coverage
            </div>
            <div className="text-2xl font-semibold">
              {(data?.sitemapChunkCount ?? 0).toLocaleString()}
              <span className="text-sm font-normal text-muted-foreground"> chunks built</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              ~{((data?.sitemapChunkCount ?? 0) * 5000).toLocaleString()} provider URLs
              <br />
              Discovered by Google from the root index.
            </div>
          </div>
        </div>

        {/* 14-day activity series */}
        <div className="rounded-md card-surface">
          <div className="p-4 border-b">
            <h2 className="font-semibold font-display">URL submission activity · last 14 days</h2>
            <p className="text-xs text-muted-foreground mt-1">
              GSC sitemap pushes and IndexNow URL counts per day.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Day</th>
                  <th className="text-right p-3">GSC succeeded</th>
                  <th className="text-right p-3">GSC failed</th>
                  <th className="text-right p-3">IndexNow URLs</th>
                </tr>
              </thead>
              <tbody>
                {(data?.series ?? []).map((s) => (
                  <tr key={s.day} className="border-t">
                    <td className="p-3 text-xs">{s.day}</td>
                    <td className="p-3 text-right">{s.gscSucceeded.toLocaleString()}</td>
                    <td className="p-3 text-right">
                      {s.gscFailed > 0 ? (
                        <span className="text-destructive">{s.gscFailed.toLocaleString()}</span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="p-3 text-right">{s.indexnowUrls.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>


        {/* Alerts */}
        {unackAlerts.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <h2 className="font-semibold font-display">
                {unackAlerts.length} unacknowledged alert
                {unackAlerts.length === 1 ? "" : "s"}
              </h2>
            </div>
            <div className="space-y-2">
              {unackAlerts.map((a) => (
                <div
                  key={a.id}
                  className="flex items-start justify-between gap-3 rounded-md card-surface-flat p-3 text-sm"
                >
                  <div>
                    <div className="font-medium">
                      <Badge variant="destructive" className="mr-2">
                        {a.kind}
                      </Badge>
                      {a.message}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(a.created_at).toLocaleString()}
                      {a.status_code != null && ` · HTTP ${a.status_code}`}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAck(a.id)}
                  >
                    Ack
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent log */}
        <div className="rounded-md card-surface">
          <div className="p-4 border-b">
            <h2 className="font-semibold font-display">Recent activity</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Time</th>
                  <th className="text-left p-3">Kind</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Range</th>
                  <th className="text-left p-3">OK / Total</th>
                  <th className="text-left p-3">Attempt</th>
                  <th className="text-left p-3">Duration</th>
                  <th className="text-left p-3">Error</th>
                </tr>
              </thead>
              <tbody>
                {(data?.recentLogs ?? []).map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="p-3 whitespace-nowrap text-xs">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="p-3">{r.kind}</td>
                    <td className="p-3">
                      <StatusBadge status={r.endpoint_status} />
                    </td>
                    <td className="p-3">
                      {r.batch_from != null
                        ? `${r.batch_from}–${r.batch_to}`
                        : "—"}
                    </td>
                    <td className="p-3">
                      {r.kind === "submit"
                        ? `${r.succeeded ?? 0} / ${r.total_submitted ?? 0}`
                        : "—"}
                    </td>
                    <td className="p-3">{r.attempt ?? "—"}</td>
                    <td className="p-3">
                      {r.duration_ms != null ? `${r.duration_ms}ms` : "—"}
                    </td>
                    <td className="p-3 max-w-xs truncate text-xs text-muted-foreground">
                      {r.error_summary ?? ""}
                    </td>
                  </tr>
                ))}
                {(data?.recentLogs ?? []).length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="p-6 text-center text-muted-foreground"
                    >
                      No activity yet. Cron runs every 10 minutes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
